import { el, icon } from '../../lib/dom';
import { openSheet, rewireDs } from '../../lib/ds';

/**
 * Bottom sheet za dejanja na dotiku (DS `.ao-sheet`).
 *
 * ⚠️ **`.dd` je namizni vzorec.** Panel visi ob sprožilcu, meri ~240px in
 * postavke so visoke toliko, kolikor jih zahteva miška. Na telefonu ob dnu
 * zaslona je to majhen cilj tik nad medijsko vrstico — in prav tam živita
 * kebab postaje in »poišči skladbo«. Bottom sheet je isti nabor dejanj v
 * obliki, ki jo palec doseže: cel spodnji rob, postavke po 44px, zapre ga
 * poteg navzdol, tap mimo ali Esc.
 *
 * **Ena plast za vse.** Sheet je en sam element v `<body>`, ne eden na
 * vrstico: seznam s štiridesetimi postajami bi sicer nosil štirideset skritih
 * dialogov. Vsebino dobi tik pred odprtjem.
 *
 * ✅ **Odpiranje gre skozi `nesylab.sheet.open()`** (DS v4.23.0, `3bab5e6`,
 * [D2 §45](../../../docs/04-delivery/D2-ds-requests.md)). Prej se je sheet
 * odpiral izključno prek dokumentnega poslušalca na `[data-sheet-open]`, zato
 * je moral atribut postavljati odjemalec sam in računati na to, da njegov
 * poslušalec teče v ciljni fazi — pred dokumentnim. Zdaj je vrstni red naš:
 * napolni, potem odpri.
 */

export const SHEET_ID = 'nwActionSheet';

export interface SheetItem {
  /** Ikona iz DS sprita, brez predpone `#`. */
  icon: string;
  label: string;
  /**
   * Kadar vidna oznaka ne pove dovolj (»Deli {postaja}«).
   *
   * `| undefined` je zaradi `exactOptionalPropertyTypes`: brez njega bi bilo
   * treba lastnost izpuščati, ne postavljati na `undefined` — in klicatelji jo
   * berejo iz DOM, kjer je `getAttribute()` natanko to.
   */
  ariaLabel?: string | undefined;
  /** Rušilno dejanje — DS ga obarva prek `.danger`. */
  danger?: boolean;
  disabled?: boolean;
  /** Postavka kot povezava (iskanje skladbe): odpre se v novem zavihku. */
  href?: string;
  run?(): void;
}

/**
 * Ali je glavni kazalec grob (prst).
 *
 * Bere se **ob vsakem kliku**, ne enkrat ob izrisu: tablica s tipkovnico in
 * telefon z miško nista teoretična primera, prezidava seznama ob menjavi
 * kazalca pa bi bila draga in vidna.
 */
export function isCoarsePointer(): boolean {
  return window.matchMedia?.('(pointer: coarse)').matches ?? false;
}

let sheet: HTMLElement | null = null;
let heading: HTMLElement | null = null;
let items: HTMLElement | null = null;

function ensureSheet(): { items: HTMLElement; heading: HTMLElement } {
  if (sheet !== null && heading !== null && items !== null) return { items, heading };

  heading = el('h4', { id: `${SHEET_ID}Title` });
  items = el('div', { class: 'nw-sheet__items' });
  sheet = el(
    'div',
    {
      class: 'ao-sheet',
      id: SHEET_ID,
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': `${SHEET_ID}Title`,
    },
    [el('div', { class: 'grab' }), heading, items],
  );
  document.body.append(el('div', { class: 'ao-sheet-scrim', 'data-sheet': SHEET_ID }, [sheet]));

  // `initSheetDrag` teče ob `overlaysInit`; brez tega se sheet ne da zapreti
  // s potegom navzdol — kar je na dotiku prva poteza, ki jo uporabnik poskusi.
  rewireDs();
  return { items, heading };
}

/**
 * Napolni deljeni sheet. Kliči ga **pred** tem, ko ga DS odpre.
 *
 * ⚠️ **Dejanje teče po zaprtju, ne pred njim.** Vsaka postavka nosi
 * `data-sheet-close`, torej jo DS pospremi z `closeSheet()`, ta pa vrne fokus
 * na sprožilec. Če bi dejanje odprlo modal sinhrono, bi mu DS fokus takoj za
 * tem odnesel nazaj na kebab. `setTimeout(0)` postavi vrstni red prav in
 * prehodne uporabniške aktivacije ne porabi (Web Share deluje naprej).
 */
export function fillSheet(title: string, list: readonly SheetItem[]): void {
  const parts = ensureSheet();
  parts.heading.textContent = title;

  parts.items.replaceChildren(
    ...list.map((item) => {
      const body = el('span', { class: 'tx' }, [el('span', { class: 't' }, [item.label])]);
      const graphic = el('span', { class: 'ic' }, [icon(item.icon, 20)]);
      const attrs: Record<string, string> = {
        class: item.danger === true ? 'opt danger' : 'opt',
        'data-sheet-close': '',
      };
      if (item.ariaLabel !== undefined) attrs['aria-label'] = item.ariaLabel;

      if (item.href !== undefined) {
        return el(
          'a',
          { ...attrs, href: item.href, target: '_blank', rel: 'noopener noreferrer' },
          [graphic, body],
        );
      }

      const button = el('button', { ...attrs, type: 'button' }, [graphic, body]);
      if (item.disabled === true) button.disabled = true;
      const { run } = item;
      if (run !== undefined) {
        button.addEventListener('click', () => {
          setTimeout(run, 0);
        });
      }
      return button;
    }),
  );
}

export interface PickerOption {
  value: string;
  label: string;
}

export interface PickerConfig {
  title: string;
  searchLabel: string;
  searchPlaceholder: string;
  /** Besedilo, kadar iskanje po izbirah nima zadetka (P4 §6). */
  emptyText: string;
  value: string;
  options: readonly PickerOption[];
  /**
   * Iskalno polje nad seznamom. Privzeto `true`.
   *
   * Pri štirih izbirah (razvrstitev) je polje več škode kot koristi: vzame
   * vrstico, na dotiku odpre tipkovnico in ni česa iskati.
   */
  searchable?: boolean;
  onPick(value: string): void;
}

/**
 * Sheet kot izbirnik z iskanjem — 241 držav v ~45 % širine polja ni izbira,
 * ampak ugibanje.
 *
 * ⚠️ **Postavke so gumbi, ne `listbox`.** Vloga `option` prinese obljubo
 * tipkovnične navigacije s puščicami, `aria-activedescendant` in tipkanja po
 * začetnicah — vse to ima vrstični `.cmb` iz DS in tu bi bilo drugič napisano,
 * slabše. Gumb ima tipkovnico brez naše kode. Na napravi z grobim kazalcem in
 * tipkovnico ostane vrstični combobox dosegljiv s puščico navzdol.
 */
export function fillPicker(config: PickerConfig): void {
  const parts = ensureSheet();
  parts.heading.textContent = config.title;

  const search = el('input', {
    class: 'input nw-sheet__search',
    type: 'search',
    autocomplete: 'off',
    'aria-label': config.searchLabel,
    placeholder: config.searchPlaceholder,
  });

  const empty = el('p', { class: 'nw-sheet__empty', hidden: '' }, [config.emptyText]);

  const options = config.options.map((option) => {
    const chosen = option.value === config.value;
    const button = el(
      'button',
      {
        class: 'opt',
        type: 'button',
        role: 'radio',
        'aria-checked': String(chosen),
        'data-sheet-close': '',
        'data-label': option.label.toLocaleLowerCase(),
      },
      [
        el('span', { class: 'ic' }, chosen ? [icon('i-check', 18)] : []),
        el('span', { class: 'tx' }, [el('span', { class: 't' }, [option.label])]),
      ],
    );
    button.addEventListener('click', () => {
      setTimeout(() => {
        config.onPick(option.value);
      }, 0);
    });
    return button;
  });

  /**
   * ⚠️ **Izbira je izbira ENE možnosti, zato `radiogroup`, ne seznam gumbov.**
   * Bralnik tako pove »2 od 4« in katera je izbrana; kljukica sama je vidna
   * informacija in nič več. Puščic ne vežemo — postavke so gumbi in jih doseže
   * tabulator; vzorec APG za radio bi zahteval svojo tipkovnično navigacijo,
   * ki bi bila druga izvedba istega pravila (glej `.cmb` v DS).
   */
  const list = el(
    'div',
    { class: 'nw-sheet__list', role: 'radiogroup', 'aria-label': config.title },
    [...options, empty],
  );

  search.addEventListener('input', () => {
    const query = search.value.trim().toLocaleLowerCase();
    let shown = 0;
    for (const button of options) {
      const hit = query === '' || (button.dataset.label ?? '').includes(query);
      button.toggleAttribute('hidden', !hit);
      if (hit) shown += 1;
    }
    empty.toggleAttribute('hidden', shown > 0);
  });

  // Iskanje po štirih izbirah je več škode kot koristi (glej `searchable`).
  parts.items.replaceChildren(...(config.searchable === false ? [list] : [search, list]));
}

/**
 * Poravna sprožilec s trenutnim načinom kazalca in pove, kateri je.
 *
 * ⛔ **`data-menu` sme stati samo pri miški.** Z njim `menu.js` odpre `.dd`; na
 * dotiku bi se ob sheetu odprl še panel in visel nad scrimom.
 *
 * `aria-haspopup` sledi temu, kar se res odpre: `dialog` za sheet, `true` za
 * meni. Testi ga uporabljajo kot edini izbirnik, ki drži v obeh načinih.
 */
export function syncSheetTrigger(button: HTMLElement): boolean {
  const coarse = isCoarsePointer();
  if (coarse) {
    button.removeAttribute('data-menu');
    button.removeAttribute('aria-expanded');
    button.setAttribute('aria-haspopup', 'dialog');
  } else {
    button.setAttribute('data-menu', '');
    button.setAttribute('aria-haspopup', 'true');
    if (!button.hasAttribute('aria-expanded')) button.setAttribute('aria-expanded', 'false');
  }
  return coarse;
}

/**
 * Odpre napolnjen sheet in pove DS, kam vrniti fokus ob zaprtju.
 *
 * `trigger` mora biti fokusabilen — DS ga ob zaprtju fokusira. Pri comboboxu
 * je to polje, ne ovoj: ovoj fokusa ne sprejme in fokus bi padel na `<body>`.
 */
export function presentSheet(trigger: HTMLElement): void {
  openSheet(SHEET_ID, trigger);
}
